import { useCallback, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeft, Trophy, Flame, Sparkles, BadgeCheck, FolderOpen, ScanLine, Archive,
  PenTool, Feather, Bell, Calendar, CreditCard, UserCheck, Lock, Loader2
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Sparkles, UserCheck, BadgeCheck, FolderOpen, ScanLine, Archive,
  PenTool, Feather, Bell, Calendar, CreditCard, Flame, Trophy,
};

export const GamificationPage = () => {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchGamification = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gamification/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchGamification(); }, [fetchGamification]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#FAFBFC] text-black'}`}>
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { xp, level, streak, badges, badges_unlocked_count, badges_total_count } = data;
  const unlocked = badges.filter((b) => b.unlocked);
  const locked = badges.filter((b) => !b.unlocked);
  const categories = [...new Set(badges.map((b) => b.category))];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#FAFBFC] text-black'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 backdrop-blur-xl ${isDark ? 'bg-[#0F0F1A]/80 border-white/10' : 'bg-white/80 border-black/10'} border-b`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-70 transition" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" /> <span className="text-sm">Retour</span>
          </Link>
          <h1 className="text-lg font-medium">Récompenses</h1>
          <div className="w-20" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10"
          data-testid="gamification-stats"
        >
          {/* Level */}
          <Card className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#81d8d0] to-[#5fb8b0] flex items-center justify-center">
                <Trophy className="w-5 h-5 text-black" />
              </div>
              <div>
                <div className="text-xs opacity-60">Niveau {level.level}</div>
                <div className="font-medium">{level.title}</div>
              </div>
            </div>
            <Progress value={level.progress_percent} className="h-2 mb-2" />
            <div className="text-xs opacity-60">
              {level.is_max ? 'Niveau max atteint !' : `${xp} / ${level.next_threshold} XP`}
            </div>
          </Card>

          {/* XP */}
          <Card className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-yellow-500" />
              </div>
              <div className="text-xs opacity-60">Expérience totale</div>
            </div>
            <div className="text-3xl font-light" data-testid="gamification-xp">{xp} <span className="text-base opacity-50">XP</span></div>
          </Card>

          {/* Streak */}
          <Card className={`p-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-500" />
              </div>
              <div className="text-xs opacity-60">Streak actuelle</div>
            </div>
            <div className="text-3xl font-light" data-testid="gamification-streak">
              {streak.current} <span className="text-base opacity-50">jour{streak.current > 1 ? 's' : ''}</span>
            </div>
            <div className="text-xs opacity-60 mt-1">Record : {streak.longest}</div>
          </Card>
        </motion.div>

        {/* Badges summary */}
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-2xl font-light">Badges</h2>
          <div className="text-sm opacity-60" data-testid="badges-count">
            {badges_unlocked_count} / {badges_total_count} débloqués
          </div>
        </div>

        {/* Badges by category */}
        {categories.map((cat) => {
          const catBadges = badges.filter((b) => b.category === cat);
          return (
            <div key={cat} className="mb-10">
              <h3 className="text-xs uppercase tracking-widest opacity-50 mb-3">{cat}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {catBadges.map((b) => {
                  const Ic = iconMap[b.icon] || Sparkles;
                  return (
                    <motion.div
                      key={b.id}
                      whileHover={{ y: -2 }}
                      data-testid={`badge-${b.id}`}
                    >
                      <Card className={`p-4 transition relative overflow-hidden ${b.unlocked
                        ? (isDark ? 'bg-gradient-to-br from-[#81d8d0]/10 to-transparent border-[#81d8d0]/40' : 'bg-gradient-to-br from-[#81d8d0]/20 to-transparent border-[#81d8d0]/50')
                        : (isDark ? 'bg-white/[0.02] border-white/5 opacity-50' : 'bg-black/[0.02] border-black/5 opacity-60')
                      } border`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${b.unlocked ? 'bg-[#81d8d0]/20' : 'bg-white/5'}`}>
                            {b.unlocked ? <Ic className="w-5 h-5 text-[#81d8d0]" /> : <Lock className="w-4 h-4 opacity-40" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{b.name}</div>
                            <div className="text-xs opacity-60 line-clamp-2 mt-0.5">{b.description}</div>
                            <div className="text-[10px] opacity-50 mt-1">+{b.xp} XP</div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
